'use client';

import type { InitOptions, i18n } from 'i18next';

import { initializeI18nClient } from './i18n.client';

let i18nInstance: i18n;
let i18nInstanceKey: string | undefined;
let pendingInstance: {
  language: InitOptions['lng'];
  key: string | undefined;
  promise: Promise<void>;
} | undefined;

type Resolver = (
  lang: string,
  namespace: string,
) => Promise<Record<string, string>>;

export function I18nProvider({
  settings,
  children,
  resolver,
  instanceKey,
}: React.PropsWithChildren<{
  settings: InitOptions;
  resolver: Resolver;
  /** Identifies what the resolver returns beyond the language (e.g. business-type vocabulary). */
  instanceKey?: string;
}>) {
  useI18nClient(settings, resolver, instanceKey);

  return children;
}

/**
 * @name useI18nClient
 * @description A hook that initializes the i18n client.
 * @param settings
 * @param resolver
 */
function useI18nClient(settings: InitOptions, resolver: Resolver, instanceKey?: string) {
  // Re-initialise only for a language or resolver-output change. Namespaces are
  // loaded on demand (i18next grows `options.ns` as they arrive), so comparing
  // namespace counts would tear down and re-suspend the whole app on the first
  // lazy load. The instance is module-global, so during SSR it is shared by
  // every request: without the key a studio tenant would server-render the
  // sports vocabulary cached by an earlier request and fail hydration.
  if (!i18nInstance || i18nInstance.language !== settings.lng || i18nInstanceKey !== instanceKey) {
    if (!pendingInstance || pendingInstance.language !== settings.lng || pendingInstance.key !== instanceKey) {
      const promise = loadI18nInstance(settings, resolver, instanceKey);
      pendingInstance = { language: settings.lng, key: instanceKey, promise };
      void promise.finally(() => {
        if (pendingInstance?.promise === promise) {
          pendingInstance = undefined;
        }
      }).catch(() => undefined);
    }

    throw pendingInstance.promise;
  }

  return i18nInstance;
}

async function loadI18nInstance(settings: InitOptions, resolver: Resolver, instanceKey?: string) {
  i18nInstance = await initializeI18nClient(settings, resolver);
  i18nInstanceKey = instanceKey;
}
